import { isEmailValid } from '@hapi/address';

let isFCPExtension = null;
let adobeHostEnvironment = null;
let hasLoadedAdobeHostEnvironment = false;
const knownFCPMessageHandlers = new Set();

const PREMIERE_PRO_APP_ID = 'PPRO';
const MIN_SUPPORTED_PREMIERE_PRO_VERSION = '24.0';
const UNSUPPORTED_BROWSER_NAME = 'Unsupported browser';
const SUPPORTED_BROWSERS = [
  { name: 'Google Chrome', icon: 'mdi-google-chrome', minimumVersion: '111' },
  { name: 'Microsoft Edge', icon: 'mdi-microsoft-edge', minimumVersion: '111' },
  { name: 'Mozilla Firefox', icon: 'mdi-firefox', minimumVersion: '121' },
  { name: 'Safari', icon: 'mdi-apple-safari', minimumVersion: '16.2' },
];

function supportsCss() {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function';
}

function supportsCssCondition(condition, value) {
  if (!supportsCss()) {
    return false;
  }

  try {
    return typeof value === 'undefined'
      ? CSS.supports(condition)
      : CSS.supports(condition, value);
  } catch {
    return false;
  }
}

// Feature detection keeps embedded WebKit environments supported even when the UA is non-standard.
const BROWSER_BASELINE_FEATURE_CHECKS = [
  function supportsCssLayers() {
    return typeof CSSLayerBlockRule !== 'undefined'
      && typeof CSSLayerStatementRule !== 'undefined';
  },
  function supportsCssHas() {
    return supportsCssCondition('selector(:has(*))');
  },
  function supportsContainerQueries() {
    return supportsCssCondition('container-type: inline-size');
  },
  function supportsColorMix() {
    return supportsCssCondition('color', 'color-mix(in srgb, white, black)');
  },
  function supportsDynamicViewportUnits() {
    return supportsCssCondition('height: 100dvh')
      && supportsCssCondition('height: 100svh')
      && supportsCssCondition('height: 100lvh');
  },
];

function parseVersionParts(version) {
  if (typeof version !== 'string' || version.trim() === '') {
    return null;
  }

  const versionParts = version.split('.').map((part) => Number.parseInt(part, 10));
  if (versionParts.some((part) => Number.isNaN(part))) {
    return null;
  }

  return versionParts;
}

function compareVersionParts(currentVersion, minimumVersion) {
  const maxLength = Math.max(currentVersion.length, minimumVersion.length);

  for (let index = 0; index < maxLength; index += 1) {
    const currentPart = currentVersion[index] ?? 0;
    const minimumPart = minimumVersion[index] ?? 0;

    if (currentPart > minimumPart) {
      return 1;
    }

    if (currentPart < minimumPart) {
      return -1;
    }
  }

  return 0;
}

function getBrowserUserAgent() {
  if (typeof navigator === 'undefined') {
    return '';
  }

  return navigator.userAgent || '';
}

function isBrowserFeatureSupported() {
  return BROWSER_BASELINE_FEATURE_CHECKS.every((check) => check());
}

function getBrowserInfo() {
  const userAgent = getBrowserUserAgent();
  const isSupported = isBrowserFeatureSupported();
  const browserMatchers = [
    {
      name: 'Microsoft Edge',
      predicate: /Edg\//i,
    },
    {
      name: 'Google Chrome',
      predicate: /Chrome\/|Chromium\/|CriOS\//i,
    },
    {
      name: 'Mozilla Firefox',
      predicate: /Firefox\/|FriOS\//i,
    },
    {
      name: 'Safari',
      predicate: /Safari\//i,
      exclude: /Chrome\/|Chromium\/|CriOS\/|FriOS\/|Edg\//i,
    },
  ];

  const matchedBrowser = browserMatchers.find((browser) => {
    if (browser.exclude?.test(userAgent)) {
      return false;
    }

    return browser.predicate.test(userAgent);
  });

  if (!matchedBrowser) {
    return {
      name: UNSUPPORTED_BROWSER_NAME,
      isSupported,
    };
  }

  return {
    name: matchedBrowser.name,
    isSupported,
  };
}

const Helper = {
  generatePassword: function(pwdLength = 10) {
    const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
    const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const specialChars = "!@#$%^&*_-";
    const digitChars = "0123456789";

    const randomChars = {
      lowercase: lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)],
      uppercase: uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)],
      special: specialChars[Math.floor(Math.random() * specialChars.length)],
      digit: digitChars[Math.floor(Math.random() * digitChars.length)],
    };

    // Initialize password with characters from each category
    let password = randomChars.lowercase + randomChars.uppercase + randomChars.special + randomChars.digit;

    // Fill remaining characters using combined character set
    const combinedChars = lowercaseChars + uppercaseChars + specialChars + digitChars;
    for (let i = 4; i < pwdLength; i++) {
      password += combinedChars[Math.floor(Math.random() * combinedChars.length)];
    }

    // Optionally shuffle the password to prevent predictability
    password = password.split("").sort(() => Math.random() - 0.5).join("");

    return password;
  },
  isFCPExtension: function() {
    if (isFCPExtension === null) {
      isFCPExtension = 'webkit' in window
        && 'messageHandlers' in window.webkit
        && typeof window.webkit.messageHandlers === 'object'
        && window.webkit.messageHandlers !== null;
    }

    return isFCPExtension;
  },

  isAdobeExtension: function() {
    return typeof window !== 'undefined' && !!window.cep;
  },

  getAdobeHostEnvironment: function() {
    if (!this.isAdobeExtension()) {
      return null;
    }

    if (hasLoadedAdobeHostEnvironment) {
      return adobeHostEnvironment;
    }

    hasLoadedAdobeHostEnvironment = true;

    if (typeof window.CSInterface !== 'function') {
      adobeHostEnvironment = null;
      return adobeHostEnvironment;
    }

    try {
      const csInterface = new window.CSInterface();
      adobeHostEnvironment = csInterface.getHostEnvironment?.() || csInterface.hostEnvironment || null;
    } catch (error) {
      console.error(error);
      adobeHostEnvironment = null;
    }

    return adobeHostEnvironment;
  },

  getAdobeAppId: function() {
    return this.getAdobeHostEnvironment()?.appId || null;
  },

  getAdobeAppVersion: function() {
    return this.getAdobeHostEnvironment()?.appVersion || null;
  },

  getMinimumSupportedPremiereProVersion: function() {
    return MIN_SUPPORTED_PREMIERE_PRO_VERSION;
  },

  getSupportedBrowsers: function() {
    return SUPPORTED_BROWSERS;
  },

  getBrowserInfo: function() {
    return getBrowserInfo();
  },

  getBrowserName: function() {
    return this.getBrowserInfo().name;
  },

  getBrowserVersion: function() {
    return null;
  },

  isBrowserSupported: function() {
    if (this.isPremiereProHost()) {
      return true;
    }

    return this.getBrowserInfo().isSupported;
  },

  isUnsupportedBrowser: function() {
    return !this.isBrowserSupported();
  },

  isPremiereProHost: function() {
    return this.getAdobeAppId() === PREMIERE_PRO_APP_ID;
  },

  isPremiereProVersionSupported: function() {
    if (!this.isPremiereProHost()) {
      return true;
    }

    const currentVersionParts = parseVersionParts(this.getAdobeAppVersion());
    const minimumVersionParts = parseVersionParts(MIN_SUPPORTED_PREMIERE_PRO_VERSION);

    if (!currentVersionParts || !minimumVersionParts) {
      return false;
    }

    return compareVersionParts(currentVersionParts, minimumVersionParts) >= 0;
  },

  isUnsupportedPremiereProVersion: function() {
    return this.isPremiereProHost() && !this.isPremiereProVersionSupported();
  },

  getPasswordRules: function(vueInstance, options = { required: true, minLength: true, specialChars: true }) {
    const minPasswordLength = 8;
    const rules = [];

    if (options.required) {
      rules.push((v) => {
        if (typeof v !== 'string' || v.trim() === '') {
          return vueInstance.$t('menus.createUser.passwordRequired');
        }

        return true;
      });
    }

    if (options.minLength) {
      rules.push((v) => {
        if (options.required) {
          if (typeof v !== 'string' || v.trim().length < minPasswordLength) {
            return vueInstance.$t('menus.createUser.passwordLength');
          }
        } else {
          if (typeof v === 'string') {
            const value = v.trim();

            if (value.trim().length > 0 && value.length < minPasswordLength) {
              return vueInstance.$t('menus.createUser.passwordLength');
            }
          }
        }

        return true;
      });
    }

    if (options.specialChars) {
      rules.push((v) => {
        if (options.required) {
          if (typeof v !== 'string' || !Helper.isValidPassword(v)) {
            return vueInstance.$t('menus.createUser.passwordInvalidChars');
          }
        } else {
          if (typeof v === 'string') {
            const value = v.trim();

            if (value.length > 0 && !Helper.isValidPassword(value)) {
              return vueInstance.$t('menus.createUser.passwordInvalidChars');
            }
          }
        }

        return true;
      });
    }

    return rules;
  },

  /**
   * Check if the password contains at least one lowercase letter, one uppercase letter, one digit, and one special character.
   * Allowed special characters are: !@#$%^&*_-
   * @param password
   * @returns {boolean}
   */
  isValidPassword: function(password) {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*_-]).*$/.test(password);
  },

  isFCPMessageHandler: function (name) {
    if (!this.isFCPExtension()) return false;

    if (knownFCPMessageHandlers.has(name)) {
      return true;
    }

    if (name in window.webkit.messageHandlers &&
      window.webkit.messageHandlers[name] &&
      typeof window.webkit.messageHandlers[name].postMessage === 'function'
    ) {
      knownFCPMessageHandlers.add(name);
      return true;
    }

    return false;
  },

  invokeFCPMessageHandler: function(name, params) {
    if (this.isFCPMessageHandler(name)) window.webkit.messageHandlers[name].postMessage(params);
  },

  isVideo: function(mimetype) {
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
  // https://www.iana.org/assignments/media-types/media-types.xhtml
  // application/mxf https://en.wikipedia.org/wiki/Material_Exchange_Format
    return mimetype.startsWith('video/') || mimetype === 'application/mxf' || mimetype === 'application/vnd.ms-asf';
  },

  isImage: function(mimetype) {
    return mimetype.startsWith('image/');
  },

  isAudio: function(mimetype) {
    return mimetype.startsWith('audio/') || mimetype === 'application/vnd.ms-asf';
  },

  getNameValidationRules: function(vueInstance, options = {
    required: true,
    messages: {
      required: 'menus.createUser.firstnameRequired',
      pattern: 'menus.createUser.invalidName',
      nameLength: 'menus.createUser.invalidName',
    },
  }) {
    const rules = [];
    const namePattern = /^(?:[A-Za-z]+\.|[A-Za-z\xC0-\uFFFF]+)(?:[ '-][a-zA-Z\xC0-\uFFFF]+| - [a-zA-Z\xC0-\uFFFF]+(?: [a-zA-Z\xC0-\uFFFF]+)*)*$/u;

    // Check if the name is required
    if (options.required) {
      rules.push((v) => {
        if (typeof v !== 'string' || v.trim() === '') {
          return vueInstance.$t(options.messages.required || 'menus.createUser.firstnameRequired');
        }

        return true;
      });
    }

    rules.push((v) => {
      return (v.length >= 2 && v.length <= 191) || vueInstance.$t(options.messages.pattern || 'menus.createUser.invalidName');
    });

    // Check if the name is valid based on the pattern
    rules.push((v) => {
      if (!namePattern.test(v)) {
          return vueInstance.$t(options.messages.pattern || 'menus.createUser.invalidName');
      }

      return true;
    });

    return rules;
  },

  getEmailValidationRules: function(vueInstance, options = {
    required: true,
    messages: {
      required: 'menus.createUser.emailRequired',
      pattern: 'menus.createUser.emailNotValid',
    },
  }) {
    const rules = [];

    // Check if the email is required
    if (options.required) {
      rules.push((v) => {
        if (typeof v !== 'string' || v.trim() === '') {
          return vueInstance.$t(options?.messages?.required || 'menus.createUser.emailRequired');
        }

        return true;
      });
    }

    // Check if the email is valid based on the pattern
    rules.push((v) => {
      if (v && !this.isEmailValid(v)) {
        return vueInstance.$t(options?.messages?.pattern || 'menus.createUser.emailNotValid');
      }

      return true;
    });

    return rules;
  },

  generateRequestId: function() {
    const timestamp = new Date().getTime();
    const randomNum = Math.floor(Math.random() * 1000000);
    return `${timestamp}-${randomNum}`;
  },

  /**
   * Cleans empty lines and whitespace-only lines from subtitle text.
   * Removes ALL consecutive empty lines and trims whitespace-only lines.
   *
   * @param {string} text - The subtitle text to clean
   * @returns {string} - Cleaned text without empty lines
   */
  cleanEmptyLines: function(text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    // Split by newlines, trim each line, filter out empty/whitespace-only lines
    const lines = text.split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    // Join back with newlines
    return lines.join('\n');
  },

  isEmailValid: function(value) {
    return isEmailValid(value, { allowUnicode: false });
  }
}

export default Helper;
